import { useState, useCallback } from 'react';
import { workbenchStore } from '~/lib/stores/workbench';
import { deployToNetlify } from '~/lib/api/netlify';
import { db, projectId } from '~/lib/persistence/useChatHistory';
import { selectionStore } from '~/lib/persistence/selectionStore';
import { checkExistingDeployment as checkExistingDeploymentUtil, ensureSiteExists } from '~/lib/utils/netlify-helpers';
import {
  netlifyStore,
  setDeploymentStatus,
  setError,
  setDeployedId,
  setAdminUrl,
  setPreviousDeployUrl,
  setHasExistingDeployment,
  setIsCheckingStatus,
  setIsErrorFixable,
} from '~/lib/stores/netlify';
import type { UseNetlifyDeploymentReturn } from '~/lib/types/netlify';
import { useStore } from '@nanostores/react';
import { getMessagesById } from '~/lib/persistence/db';

export function useNetlifyDeployment(
  accessToken: string | null | undefined,
  cleanupLogs: () => void,
): UseNetlifyDeploymentReturn {
  const [confirmDeploy, setConfirmDeploy] = useState(false);

  // Use the global store for shared state
  const store = useStore(netlifyStore);
  const { deployedId, adminUrl, hasExistingDeployment, previousDeployUrl, isCheckingStatus } = store;

  // Check for existing deployment
  const checkExistingDeployment = useCallback(async () => {
    console.log('[useNetlifyDeployment] Checking for existing deployment');

    if (!db || !projectId.get()) {
      return;
    }

    const { url, siteId, inspectDeployment } = await checkExistingDeploymentUtil(db, projectId.get());

    if (url) {
      console.log('[useNetlifyDeployment] Found existing deployment:', {
        url,
        siteId,
        inspectDeployment,
      });

      // Use global store setters instead of local state setters
      setPreviousDeployUrl(url);

      if (inspectDeployment) {
        setAdminUrl(inspectDeployment);
      }

      setHasExistingDeployment(true);

      if (siteId) {
        selectionStore.setKey('netlifySiteId', siteId);
      }
    }
  }, []);

  // Start deployment process
  const startDeployment = useCallback(async () => {
    if (!accessToken) {
      console.error('[useNetlifyDeployment] Cannot start deployment - missing accessToken');
      return;
    }

    try {
      setDeploymentStatus('new');

      const zipBlob = await workbenchStore.getZipBlob();
      let siteId: string | null = '';

      if (db && projectId.get()) {
        const projectIdData = projectId.get();
        console.log('[useNetlifyDeployment] Getting site ID from chat history, projectId:', projectIdData);

        const project = await getMessagesById(db, projectIdData!);
        const projectData = 'netlifyCreds' in project ? (project as any) : null;
        siteId = projectData?.netlifyCreds?.netlifySiteId || null;

        if (!siteId) {
          console.log('[useNetlifyDeployment] No site ID in chat history, creating new site');
          siteId = await ensureSiteExists(accessToken);
        } else {
          console.log('[useNetlifyDeployment] Using existing site ID from chat:', siteId);
        }
      } else {
        try {
          console.log('[useNetlifyDeployment] No chat DB/ID available, creating new site');
          siteId = await ensureSiteExists(accessToken);
        } catch (err) {
          if (err instanceof Error && err.message.includes('rate limit')) {
            console.error('[useNetlifyDeployment] Rate limit reached:', err.message);
            setDeploymentStatus('error');
            setError('Rate limit reached. Please wait 5 minutes before trying again.');
            setIsErrorFixable(false);
            return;
          }

          throw err;
        }
      }

      if (!siteId) {
        console.error('[useNetlifyDeployment] Failed to get or create a site ID');
        setDeploymentStatus('error');
        setError('No site ID available');
        setIsErrorFixable(false);
        return;
      }

      console.log('[useNetlifyDeployment] Deploying to Netlify with siteId:', siteId);

      const deployment = await deployToNetlify(accessToken, siteId, zipBlob);
      setDeploymentStatus('building');

      const deployId = (deployment as { deploy_id: string })?.deploy_id;

      if (!deployId) {
        console.error('[useNetlifyDeployment] No deploy_id returned from deployment');
        setDeploymentStatus('error');
        setError('No deployment ID returned from Netlify');
        setIsErrorFixable(false);
        return;
      }

      console.log('[useNetlifyDeployment] Deployment started, deployId:', deployId);

      // Update global store only, not local state
      setDeployedId(deployId);
      setIsCheckingStatus(true);
    } catch (err) {
      console.error('[useNetlifyDeployment] Deployment error:', err);
      setDeploymentStatus('error');
      setIsErrorFixable(false);

      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Failed to deploy');
      }
    }
  }, [accessToken]);

  // Handle redeploy action
  const handleRedeploy = useCallback(() => {
    console.log('[useNetlifyDeployment] Handling redeploy');

    // Always clear logs on redeploy - removed preservation logic
    cleanupLogs();

    setError(''); // Clear any previous errors
    setConfirmDeploy(true);
    console.log('[useNetlifyDeployment] Redeploy initiated, confirmDeploy set to true');
  }, [cleanupLogs]);

  return {
    deployedId,
    adminUrl,
    hasExistingDeployment,
    previousDeployUrl,
    confirmDeploy,
    isCheckingStatus,
    startDeployment,
    checkExistingDeployment,
    handleRedeploy,
    setConfirmDeploy,
  };
}
